/**
 * The decisions an option fixes, split from its trade-offs. The api appends one sentence to an option's trade-offs for what its decision effects set
 * ("On your plan: Lowers Retail checking rate (new money) to “Pay 25bp below market” for one round; keeps CD rate at “Pay at market”."); the card lifts it out and shows one row per decision.
 */
import { lowerFirst } from "../format/labels";

const FIXING = /^(Sets|Raises|Lowers|Keeps|Caps|Moves the) /;
const LEAD = /^On your plan: /;

/** One decision the option fixes and what it becomes (with its duration): "Retail checking rate (new money)" · "Lowered to “Pay 25bp below market” for one round". */
export interface OptionSetting { readonly decision: string; readonly becomes: string }

const SHAPES: readonly { readonly re: RegExp; readonly row: (m: RegExpExecArray) => OptionSetting }[] = [
  { re: /^(Lowers|Raises|Sets) (.+?) to (.+)$/i, row: (m) => ({ decision: m[2] as string, becomes: `${m[1]?.toLowerCase() === "lowers" ? "Lowered" : m[1]?.toLowerCase() === "raises" ? "Raised" : "Set"} to ${m[3]}` }) },
  { re: /^Keeps (.+?) at (.+)$/i, row: (m) => ({ decision: m[1] as string, becomes: `Kept at ${m[2]}` }) },
  { re: /^Caps (.+?) at (.+)$/i, row: (m) => ({ decision: m[1] as string, becomes: `Capped at ${m[2]}` }) },
  { re: /^Moves the (.+?) limit by (.+)$/i, row: (m) => ({ decision: `${m[1]} limit`, becomes: `Moved by ${m[2]}` }) },
  { re: /^(.+?) stays at (.+)$/i, row: (m) => ({ decision: m[1] as string, becomes: `Stays at ${m[2]}` }) },
];

/** The settings an option's last trade-off names, one row each; null when it names none or a clause is not one of the api's shapes (the sentence then stays as written). */
export function parseSettings(sentence: string): readonly OptionSetting[] | null {
  const clauses = sentence.replace(LEAD, "").replace(/\.$/, "").split("; ");
  const rows = clauses.map((c) => { for (const s of SHAPES) { const m = s.re.exec(c); if (m !== null) return s.row(m); } return null; });
  return rows.every((r) => r !== null) ? rows as OptionSetting[] : null;
}

export function splitSetting(tradeOffs: readonly string[]): { readonly settings: readonly OptionSetting[] | null; readonly rest: readonly string[] } {
  const last = tradeOffs.at(-1);
  if (last === undefined || !(LEAD.test(last) || FIXING.test(last))) return { settings: null, rest: tradeOffs };
  const settings = parseSettings(last);
  return settings === null ? { settings: null, rest: tradeOffs } : { settings, rest: tradeOffs.slice(0, -1) };
}

/**
 * Where an answer's setting of a decision changes nothing because the plan opened the year there ("Your plan opened the year with commercial property lending growth at “No hiring; wind the book down”, so this answer does not move it now."),
 * read from the rows the api wrote from the answer's own decision effects against the opening draft. A binding row (a cap, a move of a limit, "or above", a regulator's hold) is not a state and says nothing.
 * The rows that do change a setting are named only when every other row is a change too, so the sentence never claims a move that a cap or a limit does not make.
 */
export function openedAtLine(settings: readonly OptionSetting[] | null): string | null {
  if (settings === null) return null;
  const kept = settings.filter((s) => /^Kept at /.test(s.becomes) && !/ or above\b/.test(s.becomes));
  if (kept.length === 0) return null;
  const said = kept.map((s) => `${lowerFirst(s.decision)} at ${s.becomes.replace(/^Kept at /, "")}`);
  const list = said.length === 1 ? said[0] as string : `${said.slice(0, -1).join(", ")} and ${said.at(-1)}`;
  const others = settings.filter((s) => !kept.includes(s));
  const moved = others.length > 0 && others.every((s) => /^(Lowered|Raised) to /.test(s.becomes)) ? others.map((s) => lowerFirst(s.decision)) : [];
  const movedList = moved.length <= 1 ? moved.join("") : `${moved.slice(0, -1).join(", ")} and ${moved.at(-1)}`;
  return `Your plan opened the year with ${list}, so this answer does not move ${kept.length === 1 ? "it" : "them"} now${moved.length === 0 ? "" : `; of the settings it fixes, it moves ${movedList}`}.`;
}
